// Phones — the ONE rule for "is this a phone?" and "which viewport does
// this page get?".
//
// Two questions, one file, because they were answered in three places that
// fought each other: the entry file pinned every touch device to a 1280px
// desktop, and each page that had a real phone layout (first the client
// customs, then Grids & Gantts) took the viewport back for itself and put
// the pin back on the way out.
//
// 1. IS THIS A PHONE? A media query alone is not enough: a phone pinned to
//    a 1280px viewport reports 1280 while still being a phone in every way
//    that matters — no hover, a coarse pointer, a screen four inches
//    across. So: an explicit `?phone=1` (how anybody previews a phone
//    layout from a desk), OR a narrow viewport, OR a touch-only device
//    whose SHORT SIDE is phone sized whatever the viewport claims.
//
// 2. WHICH VIEWPORT? A page is either PHONE-READY — it lays itself out at
//    the device's real width — or it is not, and then a touch device gets
//    the desktop layout at a fixed 1280px, scaled to fit, to pan and zoom
//    over. The desktop panes are pinned to the viewport edges and clip
//    (body 100vh, overflow hidden all the way down), so an unready page at
//    device width is not small, it is cut off. Small but complete beats
//    broken, which is why ready is something a page EARNS:
//      · a product route graduates by being listed in PHONE_READY_ROUTES;
//      · a page that knows it is ready (a client custom, a grid) claims the
//        viewport while it is open, through `usePhone` (./usePhone.js).
//    Everything else keeps the pin.
//
// No React in here — the decisions are pure so the suite can check them
// (app-shell/test/run-phone-viewport-tests.mjs).

export const PHONE_MAX = 640;

export const VIEWPORT_DEVICE = 'width=device-width, initial-scale=1, maximum-scale=3, user-scalable=yes, viewport-fit=cover';
// width=1280 makes the phone simulate a small-tablet viewport; the browser
// auto-fits and the user pinches from there. minimum-scale=0.25 caps
// zoom-out at roughly the full-page fit on a small phone, so iOS Safari
// cannot reinterpret a past-boundary pinch-out as a pull-down (which used
// to trigger pull-to-refresh).
export const VIEWPORT_DESKTOP_PIN = 'width=1280, minimum-scale=0.25, maximum-scale=3, user-scalable=yes';

// The product routes that lay themselves out at device width. An entry
// covers the path and everything under it ('/alerts' covers '/alerts/x');
// '/' is the landing page alone. A route is added here in the same change
// that gives it a phone layout, and the phone walk (web/test/run-phone-walk-tests.mjs)
// is what says it holds at 360 / 390 / 768 / 1024.
export const PHONE_READY_ROUTES = ['/', '/auth', '/alerts', '/profile'];

// ---- is this a phone? ----

// The decision, with nothing to look it up in — so it can be checked.
//
//   search      the URL's query string (?phone=1 forces it, for previewing)
//   viewportW   what the page thinks it is
//   coarse      pointer: coarse AND hover: none — a touch screen, no mouse
//   shortSide   the SCREEN's short side, which a viewport pin cannot change
export function phoneFrom({ search = '', viewportW = 9999, coarse = false, shortSide = 9999, max = PHONE_MAX } = {}) {
  if (/[?&]phone=1/.test(String(search))) return true;
  if (viewportW <= max) return true;
  // The case a media query misses: a phone told to pretend it is a desktop.
  return !!coarse && shortSide <= max;
}

const mq = (q) => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(q).matches : false);

const read = () => {
  if (typeof window === 'undefined') return { search: '', viewportW: 9999, coarse: false, shortSide: 9999 };
  const screenW = (window.screen && window.screen.width) || 9999;
  const screenH = (window.screen && window.screen.height) || 9999;
  return {
    search: window.location.search || '',
    viewportW: window.innerWidth || 9999,
    coarse: mq('(pointer: coarse)') && mq('(hover: none)'),
    shortSide: Math.min(screenW, screenH),
  };
};

export const detectPhone = (max = PHONE_MAX) => phoneFrom({ ...read(), max });

// A REAL touch device, as opposed to a narrow window on a desk (`?phone=1`
// counts, so the phone layout can be previewed from one).
export const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return mq('(hover: none) and (pointer: coarse)') || /[?&]phone=1/.test(window.location.search || '');
};

// ---- which viewport? ----

const clean = (pathname) => {
  const p = String(pathname || '/').split(/[?#]/)[0].replace(/\/+$/, '');
  return p || '/';
};

export function isPhoneReadyPath(pathname, routes = PHONE_READY_ROUTES) {
  const p = clean(pathname);
  return routes.some((r) => {
    const route = clean(r);
    if (route === '/') return p === '/';
    return p === route || p.startsWith(route + '/');
  });
}

// What the viewport tag should say for the page in front of the user, or
// null to leave it alone (a desktop browser always keeps what index.html
// gave it).
//
//   pathname   the page in front of the user
//   touch      a touch-only device
//   claimed    the paths open pages have claimed (see claimPhoneViewport)
export function viewportFor({ pathname = '/', touch = false, claimed = [], routes = PHONE_READY_ROUTES } = {}) {
  if (!touch) return null;
  const p = clean(pathname);
  if (isPhoneReadyPath(p, routes)) return VIEWPORT_DEVICE;
  for (const c of claimed) if (clean(c) === p) return VIEWPORT_DEVICE;
  return VIEWPORT_DESKTOP_PIN;
}

// ---- the shell's side: one writer of the viewport tag ----

// path → how many open pages claim it. A tab stays mounted while another
// is in front, so a claim is kept against the claiming page's OWN path and
// only counts while that path is the one in front.
const claims = new Map();
let apply = () => {};

// A page with a phone layout asks for device width for as long as it is
// open. Returns the release.
export function claimPhoneViewport(pathname) {
  const p = clean(pathname);
  claims.set(p, (claims.get(p) || 0) + 1);
  apply();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const n = (claims.get(p) || 1) - 1;
    if (n > 0) claims.set(p, n); else claims.delete(p);
    apply();
  };
}

// Called once, from the entry file, before the app mounts. Sets the tag for
// the first page and again on every navigation.
export function installPhoneViewport() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // Desktop browsers always use device-width. The real media query, not
  // isTouchDevice: `?phone=1` on a desk previews a layout, it does not
  // make the desk a phone.
  if (!mq('(hover: none) and (pointer: coarse)')) return;
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;

  apply = () => {
    const want = viewportFor({ pathname: window.location.pathname, touch: true, claimed: claims.keys() });
    if (want && meta.getAttribute('content') !== want) meta.setAttribute('content', want);
  };
  apply();

  // SPA navigation. react-router uses history.pushState, which doesn't
  // fire popstate, so push/replace are wrapped; popstate covers the
  // back/forward case.
  const origPush = window.history.pushState;
  const origReplace = window.history.replaceState;
  window.history.pushState = function (...args) { const r = origPush.apply(this, args); apply(); return r; };
  window.history.replaceState = function (...args) { const r = origReplace.apply(this, args); apply(); return r; };
  window.addEventListener('popstate', apply);
}
